import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Share2, Loader2, Trash2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTelegram } from '@/hooks/useTelegram';
import { RATING_META, type WorkoutSession, type ExerciseLog } from '@/types/workout';

export default function WorkoutLogScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const [session, setSession] = useState<WorkoutSession | null>(null);
  const [logs, setLogs] = useState<ExerciseLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      const { data: s } = await supabase
        .from('workout_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      const { data: l } = await supabase
        .from('exercise_logs')
        .select('*, exercise:exercises(*)')
        .eq('session_id', sessionId)
        .order('position');

      setSession(s);
      setLogs(l ?? []);
      setLoading(false);
    })();
  }, [sessionId]);

  async function shareSession() {
    if (!session) return;
    haptic('light');
    const text = buildShareText(session, logs);
    if (navigator.share) {
      await navigator.share({ text });
    } else {
      await navigator.clipboard.writeText(text);
      alert('Скопировано в буфер!');
    }
  }

  async function deleteSession() {
    if (!session) return;
    setDeleting(true);
    await supabase.from('workout_sessions').delete().eq('id', session.id);
    haptic('success');
    nav('/chat?tab=workout', { replace: true });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-glow" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-4xl mb-3">🤔</div>
          <div className="font-semibold">Тренировка не найдена</div>
          <button onClick={() => nav(-1)} className="mt-4 text-emerald-glow text-sm">← Назад</button>
        </div>
      </div>
    );
  }

  const ratingMeta = RATING_META.find((r) => r.value === session.rating);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Хедер */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        <button onClick={() => nav('/chat?tab=workout')} className="p-2 -ml-2">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{session.session_name ?? 'Тренировка'}</div>
          <div className="text-[10px] text-ink-dim">{formatDate(session.started_at)}</div>
        </div>
        <button
          onClick={shareSession}
          className="p-2 rounded-xl border border-line active:bg-bg-elevated"
        >
          <Share2 size={16} />
        </button>
        <button
          onClick={() => { haptic('light'); setConfirmDelete(true); }}
          className="p-2 rounded-xl border border-rose-500/30 text-rose-400 active:bg-rose-500/10"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Подтверждение удаления */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mx-4 mb-2 bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 flex items-center gap-3"
          >
            <div className="flex-1 text-[12px] text-ink-muted">Удалить эту тренировку?</div>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-[11px] text-ink-dim px-2 py-1 border border-line rounded-lg"
            >
              Отмена
            </button>
            <button
              onClick={deleteSession}
              disabled={deleting}
              className="text-[11px] text-rose-400 font-semibold px-2 py-1 border border-rose-500/40 rounded-lg flex items-center gap-1"
            >
              {deleting ? <Loader2 size={11} className="animate-spin" /> : null}
              Удалить
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-4">
        {/* Итоги тренировки */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-emerald-glow/15 to-sky-glow/5 border border-emerald-glow/20 rounded-2xl p-4"
        >
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="font-bold text-base">{Math.round(session.total_tonnage_kg)} кг</div>
              <div className="text-[10px] text-ink-dim">Тоннаж</div>
            </div>
            <div>
              <div className="font-bold text-base">{session.total_sets}</div>
              <div className="text-[10px] text-ink-dim">Подходов</div>
            </div>
            <div>
              <div className="font-bold text-base">{formatDuration(session.duration_seconds)}</div>
              <div className="text-[10px] text-ink-dim">Время</div>
            </div>
          </div>

          {ratingMeta && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
              <span className="text-xl">{ratingMeta.emoji}</span>
              <span className="text-[12px] text-ink-muted">{ratingMeta.label}</span>
              {session.notes && (
                <span className="text-[11px] text-ink-dim ml-1">· {session.notes}</span>
              )}
            </div>
          )}
        </motion.div>

        {/* Упражнения */}
        <div className="space-y-3">
          {logs.map((log, i) => {
            const pr = log.estimated_1rm;
            return (
              <motion.div
                key={log.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="bg-bg-card border border-line rounded-xl overflow-hidden"
              >
                <div className="flex items-center gap-3 p-3">
                  {(log.exercise as any)?.gif_url && (
                    <img
                      src={(log.exercise as any).gif_url}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover bg-bg-elevated shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {(log.exercise as any)?.name ?? log.exercise_id}
                    </div>
                    <div className="text-[10px] text-ink-dim mt-0.5">
                      {log.actual_sets} подх · {log.total_reps} повт · {Math.round(log.total_tonnage_kg)} кг
                    </div>
                  </div>
                  {pr && (
                    <div className="text-right shrink-0">
                      <div className="text-[10px] text-ink-dim">е1ПМ</div>
                      <div className="text-sm font-bold text-emerald-glow">{pr} кг</div>
                    </div>
                  )}
                </div>

                {/* Подходы */}
                {log.sets_data.length > 0 && (
                  <div className="border-t border-line px-3 py-2 space-y-1">
                    {log.sets_data.map((set, si) => (
                      <div key={si} className="flex items-center gap-2 text-[11px]">
                        <span className="text-ink-dim w-4">{si + 1}</span>
                        <span className="font-medium">{set.reps} повт</span>
                        {set.weight_kg && (
                          <span className="text-ink-muted">× {set.weight_kg} кг</span>
                        )}
                        {set.weight_kg && (
                          <span className="text-ink-dim ml-auto">
                            {Math.round(set.reps * set.weight_kg)} кг объём
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  return `${m} мин`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function buildShareText(session: WorkoutSession, logs: ExerciseLog[]): string {
  const tonnage = Math.round(session.total_tonnage_kg);
  const duration = session.duration_seconds ? Math.floor(session.duration_seconds / 60) : 0;
  const lines = [
    `💪 ${session.session_name ?? 'Тренировка'} | ${formatDate(session.started_at)}`,
    ``,
    `⚡ Тоннаж: ${tonnage} кг`,
    `🔢 Подходов: ${session.total_sets}`,
    `⏱ Время: ${duration} мин`,
  ];

  const prs = logs.filter((l) => l.estimated_1rm);
  if (prs.length) {
    lines.push(``, `🏆 Расчётные максимумы:`);
    prs.forEach((l) => {
      lines.push(`  ${(l.exercise as any)?.name ?? l.exercise_id}: ${l.estimated_1rm} кг`);
    });
  }

  lines.push(``, `#KokorinHealth #тренировка`);
  return lines.join('\n');
}
